import React, { useEffect, useState, useCallback } from "react";
import { Eye, TrendingUp, RefreshCw } from "lucide-react";
import { api } from "../lib/api";

/**
 * Owner-only Preview Insights.
 * - Aggregates preview funnel for the current ISO week (no PII).
 * - Server-side endpoint enforces `company_settings` permission, so this
 *   component is also rendered only inside the Owner-only Admin tab.
 */
export default function PreviewInsightsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get("/admin/preview-insights");
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load preview insights.");
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="p-6 text-center text-[#071A2D]/60">Loading preview insights…</div>;
  }
  if (err) {
    return (
      <div data-testid="preview-insights-error" className="p-4 rounded-2xl bg-[#FF7A45]/10 border border-[#FF7A45]/30 text-[#FF7A45] text-sm">
        {err}
      </div>
    );
  }
  if (!data) return null;

  const w = data.weekly || {};
  const a = data.all_time || {};

  return (
    <div data-testid="preview-insights-panel" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#064E3B] font-semibold flex items-center gap-1.5">
            <Eye size={12}/> Preview Insights
          </div>
          <div className="text-sm text-[#071A2D]/70 mt-0.5">
            How many visitors explored TREL and converted into real accounts. Week {data.week_key}.
          </div>
        </div>
        <button
          data-testid="preview-insights-refresh"
          onClick={load}
          className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#071A2D]/10 hover:bg-[#FFF8EE] flex items-center gap-1"
        >
          <RefreshCw size={12}/> Refresh
        </button>
      </div>

      {/* Headline week card */}
      <div className="p-5 rounded-2xl bg-[#071A2D] text-[#FFF8EE] border border-[#C9A45C]/30">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold">This Week</div>
        <div className="flex items-end gap-3 mt-2">
          <div data-testid="insights-unique-visitors" className="font-serif text-5xl leading-none">
            {w.unique_visitors ?? 0}
          </div>
          <div className="pb-1 text-sm opacity-80">Preview Visitors</div>
        </div>
      </div>

      {/* Per-role breakdown */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Stat label="Customer Preview"  value={w.customer_previews ?? 0} testid="insights-customer-previews"/>
        <Stat label="Concierge Preview" value={w.provider_previews ?? 0} testid="insights-provider-previews"/>
        <Stat label="Host Preview"      value={w.host_previews ?? 0}     testid="insights-host-previews"/>
      </div>

      {/* Conversion row */}
      <div className="grid sm:grid-cols-2 gap-3">
        <Stat label="Converted Accounts" value={w.conversions ?? 0} testid="insights-conversions" accent/>
        <Stat
          label="Conversion Rate"
          value={`${(w.conversion_rate_percent ?? 0).toFixed(1)}%`}
          testid="insights-conversion-rate"
          icon={TrendingUp}
          accent
        />
      </div>

      {/* All-time footer */}
      <div className="text-[11px] text-[#071A2D]/55">
        All-time: <strong data-testid="insights-alltime-starts">{a.starts ?? 0}</strong> preview starts ·{" "}
        <strong data-testid="insights-alltime-conversions">{a.conversions ?? 0}</strong> conversions. Tracked anonymously — no PII collected.
      </div>
    </div>
  );
}

const Stat = ({ label, value, testid, accent, icon: Icon }) => (
  <div
    data-testid={testid}
    className={`p-4 rounded-2xl border ${
      accent
        ? "bg-[#C9A45C]/12 border-[#C9A45C]/30"
        : "bg-[#FFF8EE] border-[#071A2D]/10"
    }`}
  >
    <div className="text-[10px] uppercase tracking-widest text-[#064E3B] font-semibold flex items-center gap-1.5">
      {Icon && <Icon size={11}/>} {label}
    </div>
    <div className="text-2xl font-serif mt-1 text-[#071A2D]">{value}</div>
  </div>
);
